package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.FullScreenImageViewer
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.all_photos_empty
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.all_photos_title
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_photo_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.photo_viewer_close_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.photo_viewer_counter
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

data class AllPhotosUiState(
    val loading: Boolean = true,
    val photoUrls: List<String> = emptyList(),
)

/** Loads every photo (cover + gallery) for one business ("See All" from the detail Photos section). */
class AllPhotosViewModel(
    businessRepository: BusinessRepository,
    businessId: String,
) : ViewModel() {
    private val _state = MutableStateFlow(AllPhotosUiState())
    val state: StateFlow<AllPhotosUiState> = _state.asStateFlow()

    init {
        viewModelScope.launch {
            val business = (businessRepository.getBusiness(businessId) as? DataResult.Success)?.data
            _state.value = AllPhotosUiState(
                loading = false,
                photoUrls = (listOfNotNull(business?.coverUrl) + business?.photos.orEmpty()).distinct(),
            )
        }
    }
}

@Composable
fun AllPhotosRoute(
    businessId: String,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: AllPhotosViewModel = koinViewModel(parameters = { parametersOf(businessId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    // Which photo the full-screen viewer is open on; null = closed (transient view state).
    var viewerIndex by remember { mutableStateOf<Int?>(null) }
    viewerIndex?.let { index ->
        FullScreenImageViewer(
            imageUrls = state.photoUrls,
            startIndex = index,
            closeContentDescription = stringResource(Res.string.photo_viewer_close_a11y),
            counterFormat = stringResource(Res.string.photo_viewer_counter),
            imageContentDescription = stringResource(Res.string.business_photo_a11y),
            onDismiss = { viewerIndex = null },
        )
    }

    Column(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        Row(
            modifier = Modifier.fillMaxWidth().statusBarsPadding().navigationBarsPadding().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.business_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.all_photos_title),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
            )
        }
        when {
            state.loading -> LoadingState()
            state.photoUrls.isEmpty() -> EmptyState(
                title = stringResource(Res.string.all_photos_title),
                description = stringResource(Res.string.all_photos_empty),
            )
            else -> LazyVerticalGrid(
                columns = GridCells.Fixed(2),
                modifier = Modifier.appContentWidth(AppTheme.sizes.contentMaxWidth),
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                itemsIndexed(state.photoUrls, key = { _, url -> url }) { index, url ->
                    BusinessCover(
                        imageUrl = url,
                        contentDescription = stringResource(Res.string.business_photo_a11y),
                        modifier = Modifier
                            .aspectRatio(1f)
                            .clip(AppTheme.shapes.card)
                            .clickable { viewerIndex = index },
                    )
                }
            }
        }
    }
}
